import * as React from 'react';
import { 
  Button,
  ScrollArea,
} from '@/components/ui';
import { 
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { SortDirectionEnum } from '@/lib/engine';

export interface SortableColumn {
  name: string;
  displayName?: string;
  schema: any;
}

export interface SortColumn {
  column: SortableColumn;
  direction: typeof SortDirectionEnum[keyof typeof SortDirectionEnum];
}

interface SortColumnsProps {
  columns: SortableColumn[];
  sortedColumns: SortColumn[];
  onToggleSort: (column: SortableColumn) => void;
}

/**
 * Reusable component for sorting columns
 */
export function SortColumns({ columns, sortedColumns, onToggleSort }: SortColumnsProps) {
  // Check if a column is already in the sort order and get its direction
  const getColumnSortDirection = (column: SortableColumn) => {
    const sortItem = sortedColumns.find(item => 
      item.column.name === column.name
    );
    
    return sortItem ? sortItem.direction : null;
  };
  
  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="text-xs text-muted-foreground mb-2">
        Click on the sort button to toggle: Off → Ascending (↑) → Descending (↓) → Off
      </div>
      
      <ScrollArea className="flex-1">
        <div className="space-y-1 pr-4">
          {columns.map((column, index) => {
            const sortDirection = getColumnSortDirection(column);
            
            return (
              <div 
                key={index} 
                className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium">
                    {column.displayName || column.name}
                  </div>
                  <span className="text-xs text-muted-foreground mt-auto font-light">
                    {column.schema.type.toString()}
                  </span>
                </div>
                
                <Button
                  variant={sortDirection ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onToggleSort(column)}
                >
                  {sortDirection ? (
                    <>
                      {sortDirection === SortDirectionEnum.ASC ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )}
                    </>
                  ) : (
                    <ArrowUpDown className="h-3 w-3" />
                  )}
                </Button>
              </div>
            );
          })}
          
          {columns.length === 0 && (
            <div className="text-center p-4 text-sm text-muted-foreground">
              No columns available for sorting. Add some columns first.
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
} 